import _ from 'lodash'

/**
 * Single source of truth (web) for how an applied promotion code changes what a
 * booking's client EARNS (commission) or SAVES (discount). Every screen — admin
 * Finance tab, payment panel, additional-charge form/cards, client payment
 * information, the price breakdown modal, the promo widget, checkout — asks this
 * module instead of re-deriving the rule.
 *
 * It mirrors the client payout in rolzo-api imports/api/payouts/helpers.js
 * (getClientPayoutRates / getMeetGreetPayoutRates): once a promo is applied it
 * REPLACES the company benefits. Keep both sides in step.
 */

export const BOOKING_KIND = {
  STANDALONE: 'standalone',
  ADDON: 'addon',
  PLAIN: 'plain',
}

/**
 * Standalone VIP (the isStandaloneVip flag — see resolveVipFields), add-on VIP
 * (a booking carrying M&G legs, or a legacy add-on stored as type
 * vip_meet_greet without the flag) or a plain booking (transfer / by the hour /
 * car rental). Empty leg objects count as no leg.
 */
export const getBookingVipKind = ({
  isStandaloneVip,
  type,
  arrival,
  departure,
} = {}) => {
  if (isStandaloneVip === true) return BOOKING_KIND.STANDALONE
  if (
    type === 'vip_meet_greet' ||
    !_.isEmpty(arrival) ||
    !_.isEmpty(departure)
  ) {
    return BOOKING_KIND.ADDON
  }
  return BOOKING_KIND.PLAIN
}

/** 'commission' | 'discount' | null — the booking's billing mode from its benefits. */
export const getBillingMode = benefits => {
  if (parseFloat(benefits?.discountWeight) > 0) return 'discount'
  if (parseFloat(benefits?.commissionWeight) > 0) return 'commission'
  return null
}

/** "20.00%" / "20" / 20 → 20; anything else → 0. */
export const parseChargePercent = value =>
  parseFloat(String(value ?? '').replace('%', '')) || 0

/**
 * Promo terms for a PLAIN booking:
 *   - percentDiscComm on commission billing  → promo % commission;
 *   - fixedDiscComm on commission billing    → the fixed amount (discountedValue,
 *     already in booking currency) as commission;
 *   - the DiscComm promos on discount billing, percentageDiscount and
 *     fixedDiscount → a discount and NO commission.
 * Returns null for anything else (no promo, VIP bookings, discountCommissionSplit,
 * M&G-only promos, no billing mode) so callers keep their benefits display.
 *
 * Shape: { commissionWeight, discountWeight } plus commissionAmount /
 * discountAmount when the promo is a fixed amount rather than a percentage.
 */
export const resolvePromoTerms = ({ promoCode, benefits, vipKind }) => {
  if (
    vipKind !== BOOKING_KIND.PLAIN ||
    !promoCode ||
    !promoCode.type ||
    promoCode.serviceType === 'meetAndGreet'
  ) {
    return null
  }
  const promoWeight = parseFloat(promoCode.discountWeight) || 0
  const billingMode = getBillingMode(benefits)

  if (promoCode.type === 'percentDiscComm' && promoWeight > 0) {
    if (billingMode === 'commission') {
      return { commissionWeight: promoWeight, discountWeight: 0 }
    }
    if (billingMode === 'discount') {
      return { commissionWeight: 0, discountWeight: promoWeight }
    }
    return null
  }
  if (promoCode.type === 'fixedDiscComm') {
    const promoAmount = parseFloat(promoCode.discountedValue) || 0
    if (billingMode === 'commission') {
      return {
        commissionWeight: 0,
        commissionAmount: promoAmount,
        discountWeight: 0,
      }
    }
    if (billingMode === 'discount') {
      return {
        commissionWeight: 0,
        discountWeight: 0,
        discountAmount: promoAmount,
      }
    }
    return null
  }
  if (promoCode.type === 'percentageDiscount' && promoWeight > 0) {
    return { commissionWeight: 0, discountWeight: promoWeight }
  }
  if (promoCode.type === 'fixedDiscount') {
    return {
      commissionWeight: 0,
      discountWeight: 0,
      discountAmount: parseFloat(promoCode.discountedValue) || 0,
    }
  }
  return null
}

/**
 * VIP (standalone or add-on) with a percentage commission promo on commission
 * billing: the promo % replaces the benefits % on the VIP service / transfer.
 * 0 = not this case.
 */
export const getVipPromoCommissionWeight = ({ promoCode, benefits, vipKind }) =>
  vipKind !== BOOKING_KIND.PLAIN &&
  promoCode?.type === 'percentDiscComm' &&
  parseFloat(promoCode.discountWeight) > 0 &&
  getBillingMode(benefits) === 'commission'
    ? parseFloat(promoCode.discountWeight)
    : 0

/**
 * Add-on VIP M&G leg: a percentage commission promo that covers M&G (allModes /
 * meetAndGreet) on commission billing earns the promo % on the leg's
 * markup-free base (payout getMeetGreetPayoutRates). 0 = not this case.
 */
export const getAddonLegPromoCommissionWeight = ({
  promoCode,
  benefits,
  vipKind,
}) =>
  vipKind !== BOOKING_KIND.STANDALONE &&
  promoCode?.type === 'percentDiscComm' &&
  (promoCode?.serviceType === 'meetAndGreet' ||
    promoCode?.serviceType === 'allModes') &&
  parseFloat(promoCode.discountWeight) > 0 &&
  getBillingMode(benefits) === 'commission'
    ? parseFloat(promoCode.discountWeight)
    : 0
